import { createReader } from '@ai-indeed/reader';
import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import { IconFont } from '../../IconFont';
import './index.less';

const DATA_INDEX = 'data-index';

export const PdfRenderer: FC<{ src: string }> = (props) => {
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState(0);
  const pageContainerRef = useRef<HTMLDivElement>(null);
  const [pageList, setPageList] = useState<number[]>([]);

  const reader = useState(() =>
    createReader((reader, { onError, onPDFDocumentProxyCreated }) => {
      reader.read(props.src);
      onPDFDocumentProxyCreated((docProxy) => {
        const total = docProxy.numPages;
        const pageList = new Array(total).fill(0).map((_, index) => index);
        setPageList(pageList);
        setTotal(total);
      });
      onError((error) => console.error(error));
    }),
  )[0];

  const io = useState<IntersectionObserver>(
    () =>
      new IntersectionObserver((entries) => {
        (entries || []).forEach((entry) => {
          const target = entry.target as HTMLDivElement;
          if (entry.isIntersecting) {
            const pageNumber = Number(target.getAttribute(DATA_INDEX)) + 1;
            setCurrent(pageNumber);
            if (!reader.getPage(pageNumber)) {
              const { width, height } = entry.boundingClientRect;
              reader.createPage(target, pageNumber, width, height);
            }
          }
        });
      }),
  )[0];

  const mo = useState<MutationObserver>(
    () =>
      new MutationObserver((records) => {
        for (const record of records) {
          const target = record.addedNodes[0] as HTMLDivElement;
          io.observe(target);
        }
      }),
  )[0];

  useEffect(() => {
    if (pageContainerRef.current) {
      mo.observe(pageContainerRef.current, { childList: true });
    }
    return () => {
      io.disconnect();
      mo.disconnect();
      reader.destroy();
    };
  }, []);

  const turn = (pageNumber: number) => {
    if (!pageContainerRef.current || pageNumber > total || pageNumber < 1) {
      return;
    }
    let top = 0;
    for (let i = 1; i < pageNumber; i++) {
      const height = reader.getPage(i)?.height || 700;
      top += height;
    }
    pageContainerRef.current.scrollTo({ top: top, behavior: 'auto' });
    setCurrent(pageNumber);
  };

  const prev = () => {
    turn(current - 1);
  };

  const next = () => {
    turn(current + 1);
  };

  return (
    <div className="absolute w-full h-full pr-2">
      <div className="pm-pdf-container" ref={pageContainerRef}>
        {pageList.map((idx) => (
          <div key={idx} className="pm-page" data-index={idx}>
            <div className="pm-page__loading">
              <svg
                viewBox="0 0 1024 1024"
                version="1.1"
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
              >
                <path
                  d="M96 512c0-19.33 15.67-35 35-35s35 15.67 35 35c0 191.09 154.91 346 346 346s346-154.91 346-346-154.91-346-346-346c-19.33 0-35-15.67-35-35s15.67-35 35-35c229.75 0 416 186.25 416 416S741.75 928 512 928 96 741.75 96 512z"
                  fill="#4C7CFF"
                ></path>
              </svg>
            </div>
          </div>
        ))}
      </div>
      <div className="pm-switcher">
        <IconFont
          className={`${current === 1 ? 'pm-switcher__btn--disabled' : ''}`}
          type="icon-xiangshang"
          onClick={prev}
        ></IconFont>
        <span>
          {current}/{total}
        </span>
        <IconFont
          className={`${current === total ? 'pm-switcher__btn--disabled' : ''}`}
          type="icon-xiangxia"
          onClick={next}
        ></IconFont>
      </div>
    </div>
  );
};
